import {
  Background,
  Controls,
  MiniMap,
  ReactFlow,
  addEdge,
  useEdgesState,
  useNodesState,
  type Connection,
  type Edge,
  type Node,
  type ReactFlowInstance,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  CONDITION_OPERATOR_LABEL,
  findNode,
  inputPortsOfSchema,
  isFunctionNode,
  portKindOfNodeType,
  type ParamField,
  type WorkflowEdge,
  type WorkflowGraph,
  type WorkflowNode,
  type WorkflowNodeType,
  type WorkflowPortKind,
} from '@gw/shared';
import { CASE_COLORS, NODE_TYPE_LABEL, nodeTypes, type RFNodeData } from './nodes';

export interface NodeTargetInfo {
  /** paramsSchema cua model/nhom model ma node tro toi (de ve cong vao). */
  schema: ParamField[];
  name: string;
  outputKind: WorkflowPortKind;
}

interface Props {
  graph: WorkflowGraph;
  onChange: (g: WorkflowGraph) => void;
  /** Tra thong tin model/nhom model cho 1 node (de ve cong vao + ten). */
  resolveTarget: (node: WorkflowNode) => NodeTargetInfo | null;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  /** Node dang co loi (tu validateWorkflowGraph) -> to vien do. */
  invalidIds?: Set<string>;
}

const EDGE_COLOR: Record<string, string> = { success: '#16a34a', failure: '#dc2626', out: '#64748b', else: '#64748b' };

/** Nhan ngan gon cua 1 truong hop dieu kien (hien tren node + tren day noi). */
function caseLabel(c: { operator: string; value?: string }): string {
  const op = (CONDITION_OPERATOR_LABEL as Record<string, string>)[c.operator] || c.operator;
  return c.operator === 'empty' || c.operator === 'not_empty' ? op : `${op} "${c.value ?? ''}"`;
}

/** WorkflowGraph -> node/edge cua ReactFlow. */
function toFlow(
  graph: WorkflowGraph,
  resolveTarget: Props['resolveTarget'],
  invalidIds?: Set<string>,
): { nodes: Node[]; edges: Edge[] } {
  // Mau day noi tung nhanh dieu kien: theo thu tu case trong node (khop mau cong ra).
  const caseColorByPort = new Map<string, string>();
  const caseLabelByPort = new Map<string, string>();
  for (const n of graph.nodes) {
    (n.cases ?? []).forEach((c, i) => {
      caseColorByPort.set(`${n.id}|case:${c.id}`, CASE_COLORS[i % CASE_COLORS.length]);
      caseLabelByPort.set(`${n.id}|case:${c.id}`, caseLabel(c));
    });
  }

  const nodes: Node[] = graph.nodes.map((n) => {
    const target = n.type === 'model' || n.type === 'group' ? resolveTarget(n) : null;
    const data: RFNodeData = {
      label: n.name || NODE_TYPE_LABEL[n.type],
      nodeType: n.type,
      subtitle:
        target?.name ??
        (n.type === 'text' || n.type === 'image' || n.type === 'video'
          ? (n.template || '').slice(0, 60) || '(chưa nhập)'
          : n.type === 'condition'
            ? (n.condInput || '(chưa nhập giá trị)').slice(0, 60)
            : n.ref
              ? '(không tìm thấy — hãy chọn lại)'
              : '(chưa chọn)'),
      inputs: target ? inputPortsOfSchema(target.schema).map((p) => ({ name: p.name, label: p.label, kind: p.kind })) : [],
      outputKind: target?.outputKind ?? portKindOfNodeType(n.type) ?? 'text',
      invalid: invalidIds?.has(n.id),
      cases: (n.cases ?? []).map((c) => ({ id: c.id, label: caseLabel(c) })),
    };
    return { id: n.id, type: n.type, position: n.position, data };
  });

  const edges: Edge[] = graph.edges.map((e) => {
    const key = `${e.from}|${e.fromPort}`;
    const stroke = caseColorByPort.get(key) || EDGE_COLOR[e.fromPort] || '#64748b';
    const label =
      e.fromPort === 'failure' ? 'thất bại' : e.fromPort === 'else' ? 'còn lại' : caseLabelByPort.get(key);
    return {
      id: e.id,
      source: e.from,
      sourceHandle: e.fromPort,
      target: e.to,
      targetHandle: 'in',
      animated: e.fromPort === 'failure',
      label,
      style: { stroke, strokeWidth: 2 },
    };
  });

  return { nodes, edges };
}

let seq = 0;
export function newNodeId(): string {
  seq += 1;
  return `n${Date.now().toString(36)}${seq}`;
}

export default function WorkflowCanvas({ graph, onChange, resolveTarget, selectedId, onSelect, invalidIds }: Props) {
  const flow = useMemo(() => toFlow(graph, resolveTarget, invalidIds), [graph, resolveTarget, invalidIds]);
  const [nodes, setNodes, onNodesChange] = useNodesState(flow.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(flow.edges);
  const instance = useRef<ReactFlowInstance | null>(null);
  const wrapper = useRef<HTMLDivElement>(null);
  // Cho phep ghi de node/edge tu graph ben ngoai, TRU khi dang keo (tranh giat).
  const dragging = useRef(false);

  useEffect(() => {
    if (dragging.current) return;
    setNodes(flow.nodes);
    setEdges(flow.edges);
  }, [flow, setNodes, setEdges]);

  /** Chi cho noi: cong ra 'in' cua node dich; khong tu noi vao chinh no. */
  const isValidConnection = useCallback((c: Connection | Edge) => {
    if (!c.source || !c.target || c.source === c.target) return false;
    // Moi cong ra chi duoc 1 duong (thay the neu noi lai).
    return true;
  }, []);

  const onConnect = useCallback(
    (c: Connection) => {
      if (!c.source || !c.target) return;
      const fromPort = (c.sourceHandle as WorkflowEdge['fromPort']) || 'out';
      const src = findNode(graph, c.source);
      // Cong 'thanh cong'/'that bai' cua node model chi duoc 1 duong -> noi lai thi THAY THE.
      // Node chuc nang (Van ban/Anh/Video) la nguon du lieu, duoc noi toi NHIEU node -> GIU lai.
      const replaceExisting = !src || !isFunctionNode(src);
      const kept = graph.edges.filter((e) => {
        if (e.from === c.source && e.to === c.target && e.fromPort === fromPort) return false; // chong trung
        if (replaceExisting && e.from === c.source && e.fromPort === fromPort) return false;
        return true;
      });
      onChange({
        ...graph,
        edges: [...kept, { id: `e${Date.now().toString(36)}`, from: c.source, fromPort, to: c.target }],
      });
      setEdges((eds) => addEdge(c, eds));
    },
    [graph, onChange, setEdges],
  );

  const onNodeDragStart = useCallback(() => {
    dragging.current = true;
  }, []);

  const onNodeDragStop = useCallback(
    (_: unknown, node: Node) => {
      dragging.current = false;
      onChange({
        ...graph,
        nodes: graph.nodes.map((n) => (n.id === node.id ? { ...n, position: node.position } : n)),
      });
    },
    [graph, onChange],
  );

  const onEdgesDelete = useCallback(
    (deleted: Edge[]) => {
      const ids = new Set(deleted.map((e) => e.id));
      onChange({ ...graph, edges: graph.edges.filter((e) => !ids.has(e.id)) });
    },
    [graph, onChange],
  );

  const onNodesDelete = useCallback(
    (deleted: Node[]) => {
      const ids = new Set(deleted.map((n) => n.id));
      // Khong cho xoa Start/End: workflow bat buoc phai co.
      const removable = graph.nodes.filter((n) => ids.has(n.id) && n.type !== 'start' && n.type !== 'end').map((n) => n.id);
      if (!removable.length) return;
      const rm = new Set(removable);
      onChange({
        ...graph,
        nodes: graph.nodes.filter((n) => !rm.has(n.id)),
        edges: graph.edges.filter((e) => !rm.has(e.from) && !rm.has(e.to)),
      });
    },
    [graph, onChange],
  );

  /** Tha 1 node tu bang chon ben trai vao canvas. */
  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      const raw = event.dataTransfer.getData('application/gw-node');
      if (!raw || !instance.current) return;
      const payload = JSON.parse(raw) as { type: WorkflowNodeType; ref?: string; name?: string };
      const position = instance.current.screenToFlowPosition({ x: event.clientX, y: event.clientY });
      const node: WorkflowNode = {
        id: newNodeId(),
        type: payload.type,
        name: payload.name || NODE_TYPE_LABEL[payload.type],
        position,
        ...(payload.ref ? { ref: payload.ref } : {}),
        ...(payload.type === 'model' || payload.type === 'group'
          ? { params: {} }
          : payload.type === 'condition'
            ? { condInput: '', cases: [{ id: `c${Date.now().toString(36)}`, operator: 'equals' as const, value: '' }] }
            : { template: '' }),
      };
      onChange({ ...graph, nodes: [...graph.nodes, node] });
      onSelect(node.id);
    },
    [graph, onChange, onSelect],
  );

  return (
    <div ref={wrapper} style={{ width: '100%', height: '100%' }} onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }} onDrop={onDrop}>
      <ReactFlow
        nodes={nodes.map((n): Node => ({ ...n, selected: n.id === selectedId }))}
        edges={edges}
        nodeTypes={nodeTypes}
        onInit={(i) => { instance.current = i; }}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        isValidConnection={isValidConnection}
        onNodeDragStart={onNodeDragStart}
        onNodeDragStop={onNodeDragStop}
        onNodesDelete={onNodesDelete}
        onEdgesDelete={onEdgesDelete}
        onNodeClick={(_, n) => onSelect(n.id)}
        onPaneClick={() => onSelect(null)}
        fitView
        deleteKeyCode={['Backspace', 'Delete']}
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={16} />
        <Controls />
        <MiniMap pannable zoomable />
      </ReactFlow>
    </div>
  );
}
